import { createClient } from '@supabase/supabase-js';
import {
  markSupabaseReachable,
  markSupabaseUnreachable,
  shouldAllowSupabaseNetwork
} from '../utils/supabaseReachability';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export function isSupabaseConfigured() {
  return Boolean(supabaseUrl && supabaseAnonKey);
}

function isElectronShell() {
  return typeof window !== 'undefined' && Boolean(window.electronAPI);
}

async function supabaseFetch(input, init) {
  if (!shouldAllowSupabaseNetwork()) {
    const err = new TypeError('Supabase request skipped (offline or recent connection failure)');
    err.name = 'SupabaseOfflineError';
    throw err;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    if (response.ok || (response.status >= 400 && response.status < 500)) {
      markSupabaseReachable();
    }
    return response;
  } catch (error) {
    markSupabaseUnreachable();
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

let client = null;

function buildClient() {
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      // Refresh when network allows — supabaseFetch already short-circuits on cooldown
      // so offline POS is not flooded after a failed DNS attempt.
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: !isElectronShell()
    },
    global: {
      fetch: supabaseFetch
    }
  });
}

/** Lazy Supabase client; null when env vars are missing. */
export function getSupabase() {
  if (!isSupabaseConfigured()) {
    return null;
  }
  if (!client) {
    client = buildClient();
  }
  return client;
}

/** Backward-compatible export — methods throw only when invoked without configuration. */
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const instance = getSupabase();
      if (!instance) {
        throw new Error(
          'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env'
        );
      }
      const value = instance[prop];
      return typeof value === 'function' ? value.bind(instance) : value;
    }
  }
);

export default supabase;
